import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuickSearchModal } from '../../../../../../shared/contexts';
import { useIsSemanticSearchEnabled } from '../../../../../../shared/hooks';
import type { Suggestion } from '../types';
import { useSuggestionsText } from './useSuggestionsText';

// Module-level variable to track which set was used last
// This persists across component mounts within the same session
let lastUsedSetIndex = 0;
// Track if we've already initialized for the current dialog session
let hasInitializedForSession = false;

export const useSuggestions = () => {
  const isSemanticSearchEnabled = useIsSemanticSearchEnabled();
  const { isModalOpen } = useQuickSearchModal();
  const previousModalOpenRef = useRef(false);
  const [currentSetIndex, setCurrentSetIndex] = useState(() => {
    // Initialize with last used set (will be updated when dialog opens if needed)
    return lastUsedSetIndex;
  });

  // Access all TEXT properties at the top level via helper hook
  const text = useSuggestionsText();

  // Alternate set only when dialog transitions from closed to open
  useEffect(() => {
    if (isModalOpen && !previousModalOpenRef.current) {
      // Dialog just opened - alternate the set for this new session
      if (!hasInitializedForSession) {
        const nextSetIndex = lastUsedSetIndex === 0 ? 1 : 0;
        lastUsedSetIndex = nextSetIndex;
        setCurrentSetIndex(nextSetIndex);
        hasInitializedForSession = true;
      } else {
        // Dialog reopened but we've already initialized - keep current set
        setCurrentSetIndex(lastUsedSetIndex);
      }
    } else if (!isModalOpen && previousModalOpenRef.current) {
      // Dialog just closed - reset session flag for next open
      hasInitializedForSession = false;
    }
    previousModalOpenRef.current = isModalOpen;
  }, [isModalOpen]);

  const suggestions = useMemo(() => {
    // Create suggestions arrays using the pre-accessed TEXT values
    const suggestionsSetA: Suggestion[] = [
      // Subset 1
      {
        type: 'status',
        title: text.title.status,
        description: text.description.statusApproved,
      },
      {
        type: 'comments',
        title: text.title.comments,
        description: text.description.commentsCount,
      },
      {
        type: 'visuals',
        title: text.title.visuals,
        description: text.description.visualsCars,
      },
      {
        type: 'format',
        title: text.title.format,
        description: text.description.format,
      },
      // Subset 2
      {
        type: 'transcription',
        title: text.title.transcription,
        description: text.description.transcriptionFlour,
      },
      {
        type: 'date',
        title: text.title.date,
        description: text.description.dateDays,
      },
      {
        type: 'keyword',
        title: text.title.keyword,
        description: text.description.keywordExploratory,
      },
      {
        type: 'uploader',
        title: text.title.uploader,
        description: text.description.uploaderJeremiah,
      },
      // Subset 3
      {
        type: 'comments',
        title: text.title.comments,
        description: text.description.commentsLighting,
      },
      {
        type: 'visuals',
        title: text.title.visuals,
        description: text.description.visualsMountains,
      },
      {
        type: 'transcription',
        title: text.title.transcription,
        description: text.description.transcriptionLaunch,
      },
      {
        type: 'date',
        title: text.title.date,
        description: text.description.dateMonths,
      },
    ];

    const suggestionsSetB: Suggestion[] = [
      // Subset 1
      {
        type: 'keyword',
        title: text.title.keyword,
        description: text.description.keywordConfidential,
      },
      {
        type: 'uploader',
        title: text.title.uploader,
        description: text.description.uploaderThomas,
      },
      {
        type: 'resolution',
        title: text.title.resolution,
        description: text.description.resolution,
      },
      {
        type: 'duration',
        title: text.title.duration,
        description: text.description.durationShorter,
      },
      // Subset 2
      {
        type: 'fileSize',
        title: text.title.fileSize,
        description: text.description.fileSizeLarger,
      },
      {
        type: 'comments',
        title: text.title.comments,
        description: text.description.commentsApproved,
      },
      {
        type: 'visuals',
        title: text.title.visuals,
        description: text.description.visualsPerson,
      },
      {
        type: 'transcription',
        title: text.title.transcription,
        description: text.description.transcriptionBudget,
      },
      // Subset 3
      {
        type: 'date',
        title: text.title.date,
        description: text.description.dateBefore,
      },
      {
        type: 'keyword',
        title: text.title.keyword,
        description: text.description.keywordMarketing,
      },
      {
        type: 'assignee',
        title: text.title.assignee,
        description: text.description.assignee,
      },
      {
        type: 'fileType',
        title: text.title.fileType,
        description: text.description.fileType,
      },
    ];

    const suggestionSets = [suggestionsSetA, suggestionsSetB] as const;
    const baseSuggestions = suggestionSets[currentSetIndex];

    // Process suggestions (replace some items if semantic search is disabled)
    if (isSemanticSearchEnabled) {
      return baseSuggestions;
    }

    // Create a copy to avoid mutating the original
    const processed = [...baseSuggestions];
    const altSuggestions: Record<string, Suggestion> = {
      setA_subset1: {
        type: 'fileSize',
        title: text.title.fileSize,
        description: text.description.fileSizeSmaller,
      },
      setA_subset3: {
        type: 'duration',
        title: text.title.duration,
        description: text.description.durationLonger,
      },
      setB_subset2: {
        type: 'status',
        title: text.title.status,
        description: text.description.statusNeedsReview,
      },
    };

    if (currentSetIndex === 0) {
      // Set A: Replace visuals in Subset 1 (index 2) and Subset 3 (index 10)
      processed[2] = altSuggestions.setA_subset1;
      processed[10] = altSuggestions.setA_subset3;
    } else if (currentSetIndex === 1) {
      // Set B: Replace visuals in Subset 2 (index 6)
      processed[6] = altSuggestions.setB_subset2;
    }

    return processed;
  }, [currentSetIndex, isSemanticSearchEnabled, text]);

  return {
    suggestions,
  };
};
